import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { CompanyInfo } from 'hooks';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';



export interface AssessmentSummary {
  id: number;
  title: string;
  status: string;
  total_candidated_allowed: number;
  job_description: string;
  duration_in_minutes: number;
  company_info: CompanyInfo;
  assessment_id: string;
  created_at: string;
  updated_at: string;
}

export interface ResponseDetails {
  id: number;
  full_name: string;
  email: string;
  phone_number?: string;
  portfolio?: string;
  resume?: string;
  status: string;
  total_score: string;
  bucket: string;
  created_at: string;
  updated_at: string;
  questions_answered: number;
  duration_minutes: number;
  candidate: number;
}

export interface ReportDetailsData {
  assessment_summary: AssessmentSummary;
  response_details: ResponseDetails;
  basic_expectation?: any;
  soft_skills?: any;
  work_place_qualities?: any;
  work_style?: any;
  work_motivator?: any;
  personality?: any;
  domain_knowledge?: any;
}

interface ReportDetailsResponse {
  message: string;
  data: ReportDetailsData;
}

/**
 * Hook for fetching full report details (assessment info and candidate info)
 */
export const useReportDetails = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reportDetails', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<ReportDetailsResponse>(
        `/reports/${reportId}/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch report details';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    reportDetails: data,
    isLoading,
    error,
    refetch,
  };
};
